// @ts-nocheck
import { authenticatedFetch, BACKEND_API_URL, getClerkToken } from "./apiClient";
import {
  ApiRequestError,
  throwFromErrorBody,
  throwIfNotOk,
} from "../utils/apiRequestError";
import { parseApiErrorBody } from "../utils/parseApiError";
import type { BrokerCredentialRecord } from "../utils/brokerLiveSync";
import type {
  ColumnMapping,
  CsvImportBuiltInBroker,
  CsvImportModeOption,
  CsvImportMappableField,
  CsvImportWarningCode,
  ImportMode,
  LayoutRowGrouping,
  LayoutShape,
  ValueTransforms,
} from "../types/customCsvImport";
import type {
  BrokerSyncWithInstrumentSetupResponse,
  InstrumentSetupResponse,
} from "../types/instrumentSetup";

export interface TradedAssetClassesSchema {
  field_name?: string;
  allowed_asset_classes?: string[];
  multi?: boolean;
  ui_control?: string;
  field_labels?: Record<string, string>;
}

export interface BrokerTradedAssetClassesCapability {
  required?: boolean;
  user_selectable?: boolean;
  contexts?: string[];
  allowed_asset_classes?: string[];
  fixed_values?: string[] | null;
  ui_control?: string;
  field_labels?: Record<string, string>;
}

export interface BrokerExecutingBrokerCapability {
  required?: boolean;
  contexts?: string[];
  field_labels?: Record<string, string>;
  placeholder?: string;
  hint?: string;
}

export interface BrokerCapabilities {
  broker_name: string;
  display_name: string;
  /** Some deployments expose OANDA environment choices here instead of under `auth`. */
  oanda_environment_options?: { value: string; label: string }[];
  traded_asset_classes_schema?: TradedAssetClassesSchema;
  traded_asset_classes?: BrokerTradedAssetClassesCapability;
  executing_broker?: BrokerExecutingBrokerCapability;
  auth: {
    supports_auto_sync: boolean;
    required_fields: string[];
    optional_fields: string[];
    /** IBKR: hide account_code on connect — ClientAccountID comes from Flex report. */
    account_code_inferred_from_report?: boolean;
    /** OANDA: allowed `oanda_environment` values for credentials (when returned here). */
    oanda_environment_options?: { value: string; label: string }[];
    /** GET path for non-secret credential edit form (e.g. Binance pairs + start date). */
    edit_form_endpoint?: string;
    edit_form_safe_fields?: string[];
    edit_form_secret_fields?: string[];
    /** Tradovate: fixed asset class — no picker on connect (`["futures"]`). */
    fixed_traded_asset_classes?: string[];
    /** cTrader Open API: GET path for OAuth authorize URL (relative to API base). */
    oauth_authorize_url?: string;
    /** cTrader Open API: POST path for OAuth code exchange (relative to API base). */
    oauth_callback?: string;
  };
  csv_import?: BrokerCsvImportCapabilities;
  /** MT5 and other brokers: statement upload via .xlsx (same shape as `csv_import`). */
  xlsx_import?: BrokerCsvImportCapabilities;
  /** MT4 and other brokers: statement upload via .html/.htm (same shape as `csv_import`). */
  html_import?: BrokerCsvImportCapabilities;
  /** Broker-sync defaults (Connect / API-backed accounts). */
  analytics: TradeAccountAnalytics;
  /** CSV-import defaults for this broker (import form; not used for synced account UI). */
  csv_import_analytics?: TradeAccountAnalytics;
  /**
   * Broker-sync contract from capabilities (Binance API lookback, IBKR flex_statement, etc.).
   * See `utils/brokerTradeSync` and `utils/binanceTradeSync`.
   */
  trade_sync?: BrokerTradeSyncCapabilities;
  /** Row-by-row manual trade entry in the connect wizard. */
  manual_trade_entry?: {
    supported?: boolean;
    notes?: string | string[];
  };
}

/** Shared statement-import shape — `csv_import`, `xlsx_import`, and nested `trade_sync` variants. */
export interface BrokerCsvImportCapabilities {
  supported: boolean;
  endpoint?: string;
  accepted_file_types?: string[];
  format?: string;
  required_fields?: string[];
  optional_fields?: string[];
  date_format_options?: string[];
  time_format_options?: string[];
  field_labels?: Record<string, string>;
  /** Max days per uploaded file (IBKR Flex CSV: typically 365). */
  max_lookback_days_per_file?: number;
  /** IBKR: execution timestamps must include EST/EDT/UTC/offset in Flex exports. */
  requires_trade_timestamp_timezone?: boolean;
  /** Allowed values for `traded_asset_classes` on import (multi-select). */
  allowed_asset_classes?: string[];
  /** Tradovate: fixed class — no picker (`["futures"]`). */
  fixed_traded_asset_classes?: string[];
  notes?: string | string[];
}

/** Extended shape for `broker_name: custom_csv` — all fields from capabilities. */
export interface CustomCsvImportCapabilities extends BrokerCsvImportCapabilities {
  preview_endpoint?: string;
  /** Inspect CSV rows to pick the header row (banner / junk rows above titles). */
  inspect_endpoint?: string;
  /** Primary templates API (replaces profiles_endpoint). */
  templates_endpoint?: string;
  /** @deprecated Use templates_endpoint — still returned by older capabilities payloads. */
  profiles_endpoint?: string;
  import_modes?: CsvImportModeOption[];
  mappable_fields?: Partial<Record<ImportMode, CsvImportMappableField[]>>;
  layout_row_groupings?: LayoutRowGrouping[];
  layout_shapes?: LayoutShape[];
  built_in_brokers?: CsvImportBuiltInBroker[];
  warning_codes?: CsvImportWarningCode[];
  quantity_unit_options?: Array<"units" | "lots">;
  /**
   * Formats for `value_transforms.datetime_format`.
   * BE may send strings or `{ value, label }` objects.
   */
  datetime_format_options?: Array<string | { value?: string; label?: string }>;
}

export function isCustomCsvBroker(cap: BrokerCapabilities | null | undefined): boolean {
  return String(cap?.broker_name || "").trim().toLowerCase() === "custom_csv";
}

export interface BrokerOpenApiSyncCapabilities {
  supported?: boolean;
  strategy?: string;
  oauth_authorize_url?: string;
  oauth_callback?: string;
  sync_endpoint?: string;
  oauth_scope?: string;
  account_id_field?: string;
  rate_limits?: Record<string, number>;
  max_lookback_days_env?: string;
  notes?: string | string[];
}

export interface BrokerTradeSyncCapabilities {
  strategy?: string;
  openapi_sync?: BrokerOpenApiSyncCapabilities;
  required_credential_fields?: string[];
  optional_credential_fields?: string[];
  sync_endpoint?: string;
  sync_query_params?: Record<string, string>;
  credential_field_labels?: Record<string, string>;
  /** IBKR: ClientAccountID from Flex — do not show account_code on connect form. */
  account_code_inferred_from_report?: boolean;
  statement_time_zone_inferred_from_report?: boolean;
  /** IBKR portal checklist (display only; Tessera applies formats automatically). */
  ibkr_flex_portal_setup?: {
    summary?: string;
    description?: string;
    date_format?: string;
    time_format?: string;
    date_time_separator?: string;
    include_trades_section?: boolean;
    items?: Array<{ label?: string; value?: string; text?: string; format?: string }>;
    steps?: Array<{ label?: string; value?: string; text?: string }>;
    checklist?: Array<string | { label?: string; value?: string }>;
  };
  /** IBKR: execution timestamps must include EST/EDT/UTC/offset in Flex exports. */
  requires_trade_timestamp_timezone?: boolean;
  /** IBKR: same CSV contract as top-level `csv_import` for connect → sync → CSV fallback. */
  csv_import?: BrokerCsvImportCapabilities;
  /** MT5: Trade History Report .xlsx under `trade_sync`. */
  xlsx_import?: BrokerCsvImportCapabilities;
  /** MT4: Detailed Report .html/.htm under `trade_sync`. */
  html_import?: BrokerCsvImportCapabilities;
  api_sync_lookback_days?: number;
  /** IBKR: same role as `api_sync_lookback_days` for Flex query window. */
  flex_query_max_lookback_days?: number;
  api_sync_from_date?: string;
  api_sync_to_date?: string;
  history_before_api_requires_csv_import?: boolean;
  /** IBKR: older than Flex window → CSV path. */
  history_before_flex_query_requires_csv_import?: boolean;
  full_history_recommendation?: string;
  notes?: string | string[];
  csv_import_endpoint?: string;
  optional_account_start_date_field?: string;
  account_start_date_purpose?: string;
  user_guidance?: {
    summary?: string;
    steps?: Array<{
      action?: string;
      label?: string;
      covers?: string;
      covers_from_date?: string;
      covers_to_date?: string;
      endpoint?: string;
      required_fields?: string[];
      allowed_asset_classes?: string[];
      field_labels?: Record<string, string>;
      date_format_options?: string[];
      time_format_options?: string[];
    }>;
  };
}

/** Per-account or per-capability analytics from the API. */
export interface TradeAccountAnalytics {
  data_source?: "csv_import" | "broker_sync" | string;
  default_growth_basis?: string;
  supports_portfolio_balance_from_broker?: boolean;
  supports_portfolio_growth?: boolean;
  /** Present on CSV-import accounts when known (from stored import settings). */
  statement_time_zone?: string | null;
}

export interface BrokerAccount {
  accountCode: string;
  accountStatus?: string;
  baseCurrency?: string;
  /** Null for Binance spot sync — not an error. */
  balance?: number | null;
  isDemo?: boolean;
  customName?: string | null;
}

/** Row from GET /broker/trade-accounts */
export interface TradeAccountApiRow {
  broker_name: string;
  account_code: string;
  label: string;
  display_name: string;
  /** cTrader: platform login (user-facing account number). */
  platform_account_login?: string | null;
  platformAccountLogin?: string | null;
  /** cTrader: executing broker from Open API (FxPro, IC Markets, …). */
  executing_broker_name?: string | null;
  executingBrokerName?: string | null;
  /** cTrader: ctid used for sync (same as account_code when set). */
  ctrader_ctid?: string | null;
  ctraderCtid?: string | null;
  /** credentials / broker_list / synced_trades (API-backed) / imported_trades (CSV-only, no creds for broker) */
  source?: "credentials" | "broker_list" | "synced_trades" | "imported_trades" | string;
  /** Credential row `last_sync` for this broker (shared across accounts under that connection). */
  last_broker_sync_at?: string | null;
  lastBrokerSyncAt?: string | null;
  /** Latest CSV-backed trade activity for this (broker_name, account_code). */
  last_csv_import_at?: string | null;
  lastCsvImportAt?: string | null;
  custom_name?: string | null;
  customName?: string | null;
  is_demo?: boolean | null;
  isDemo?: boolean | null;
  analytics?: TradeAccountAnalytics;
  /** Last scan: unresolved instrument setup symbols remain. */
  instrument_setup_pending?: boolean | null;
  instrumentSetupPending?: boolean | null;
}

export interface TradeAccountsResponse {
  active: { broker_name: string; account_code: string } | null;
  accounts: TradeAccountApiRow[];
  /** Helper for cTrader UX: true when more than one cTrader account is discoverable. */
  has_multiple_ctrader_accounts?: boolean;
  /** Helper list of discoverable cTrader accounts (ctid rows). */
  ctrader_found_accounts?: TradeAccountApiRow[];
}

export const fetchTradeAccounts = async (): Promise<TradeAccountsResponse> => {
  const response = await authenticatedFetch("/broker/trade-accounts");
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to load trade accounts: ${response.status}`);
  }
  return response.json();
};

export async function fetchInstrumentSetup(
  brokerAccountCode: string
): Promise<InstrumentSetupResponse> {
  const code = brokerAccountCode.trim();
  const response = await authenticatedFetch(
    `/broker/accounts/${encodeURIComponent(code)}/instrument-setup`
  );
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to load instrument setup: ${response.status}`);
  }
  return response.json();
}

export async function scanInstrumentSetup(
  brokerAccountCode: string
): Promise<InstrumentSetupResponse> {
  const code = brokerAccountCode.trim();
  const response = await authenticatedFetch(
    `/broker/accounts/${encodeURIComponent(code)}/instrument-setup/scan`,
    { method: "POST" }
  );
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to scan instrument setup: ${response.status}`);
  }
  return response.json();
}

export const putActiveTradeAccount = async (
  brokerName: string,
  accountCode: string
): Promise<{ broker_name: string; account_code: string }> => {
  const response = await authenticatedFetch("/broker/active-trade-account", {
    method: "PUT",
    body: JSON.stringify({
      broker_name: brokerName.toLowerCase(),
      account_code: accountCode,
    }),
  });
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to set active trade account: ${response.status}`);
  }
  return response.json();
};

/** Response from DELETE /broker/accounts (Tessera data for one broker_account_code; credentials are not modified). */
export interface RemoveBrokerAccountResponse {
  broker_account_code: string;
  broker_name_filter?: string | null;
  deleted_trades?: number;
  deleted_transfers?: number;
  custom_name_rows_cleared?: number;
  deleted_pnl_milestone_definitions?: number;
  deleted_milestone_state_rows?: number;
  deleted_user_configurations?: number;
  deleted_reports?: number;
  deleted_backtests?: number;
  active_trade_account_cleared?: boolean;
  /** Custom CSV: credentials metadata pruned for this account (picker ghost fix). */
  import_metadata_pruned?: boolean;
}

function formatBrokerApiErrorDetail(error: Record<string, unknown>, fallback: string): string {
  return parseApiErrorBody(error, fallback).message || fallback;
}

function throwBrokerIntegrationFromBody(status: number, body: unknown, fallback: string): never {
  const record = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  throwFromErrorBody(status, record, formatBrokerApiErrorDetail(record, fallback));
}

function throwBrokerClientError(message: string): never {
  throw new ApiRequestError(message, 400, {
    slug: "validation.unprocessable",
    message,
  });
}

async function throwBrokerResponseNotOk(response: Response, fallback: string): Promise<never> {
  const text = await response.text().catch(() => "");
  if (!text.trim()) {
    throwBrokerIntegrationFromBody(response.status, {}, fallback);
  }
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    throwBrokerIntegrationFromBody(response.status, parsed, fallback);
  } catch (err) {
    if (err instanceof ApiRequestError) throw err;
    const recovered = await readBrokerRequestErrorMessage(
      new Response(text, { status: response.status, statusText: response.statusText }),
      fallback
    );
    throwBrokerIntegrationFromBody(response.status, { message: recovered }, recovered || fallback);
  }
}

/**
 * Prefer JSON `detail`; otherwise recover a readable line from HTML/plain bodies
 * (e.g. FastAPI 500 with ValueError in the traceback).
 */
async function readBrokerRequestErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  const text = await response.text().catch(() => "");
  if (!text?.trim()) return fallback;
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const d = formatBrokerApiErrorDetail(parsed, "");
    if (d) return d;
  } catch {
    // not JSON
  }
  const valueErr = text.match(/ValueError:\s*([^\n<]+)/i);
  if (valueErr?.[1]?.trim()) return valueErr[1].trim();
  const unsupported = text.match(/Unsupported time_format[^<\n]*/i);
  if (unsupported?.[0]) return unsupported[0].replace(/\s+/g, " ").trim();
  const trimmed = text.trim();
  if (trimmed.length > 0 && trimmed.length <= 600) return trimmed;
  return fallback;
}

/**
 * Deletes Tessera data tied to `broker_account_code` (trades, transfers, milestones, configs, reports, backtests, custom names, etc.).
 * Does not remove broker credentials or disconnect the broker — use Profile broker flows for that.
 * Pass `broker_name` when the same `broker_account_code` could exist for multiple brokers.
 */
export const deleteBrokerAccount = async (params: {
  broker_account_code: string;
  broker_name?: string;
}): Promise<RemoveBrokerAccountResponse> => {
  const q = new URLSearchParams();
  q.set("broker_account_code", params.broker_account_code);
  if (params.broker_name) {
    q.set("broker_name", params.broker_name.toLowerCase());
  }
  const response = await authenticatedFetch(`/broker/accounts?${q.toString()}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwBrokerIntegrationFromBody(
      response.status,
      error,
      `Failed to remove account: ${response.status}`
    );
  }
  return response.json();
};

const BROKER_STATEMENT_IMPORT_ENDPOINTS: Record<string, string> = {
  liquidbrokers: "/broker/liquidbrokers/csv-import",
  liquid: "/broker/liquidbrokers/csv-import",
  oanda: "/broker/oanda/csv-import",
  binance: "/broker/binance/csv-import",
  ibkr: "/broker/ibkr/csv-import",
  tradingview: "/broker/tradingview/csv-import",
  tradingview_paper: "/broker/tradingview-paper/csv-import",
  mt5: "/broker/mt5/xlsx-import",
  mt4: "/broker/mt4/html-import",
  tradovate: "/broker/tradovate/csv-import",
  ninjatrader: "/broker/ninjatrader/csv-import",
  ctrader: "/broker/ctrader/csv-import",
  hyperliquid: "/broker/hyperliquid/csv-import",
};

export const fetchBrokerCapabilities = async (): Promise<BrokerCapabilities[]> => {
  const response = await authenticatedFetch("/broker/capabilities");
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to load broker capabilities: ${response.status}`);
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.brokers)) return data.brokers;
  return [];
};

/** GET /broker/credentials/form — safe fields for update-credentials (Binance). */
export interface BinanceCredentialsFormResponse {
  broker_name: string;
  account_code?: string;
  binance_spot_symbols?: string[];
  binance_account_start_date?: string | null;
  has_binance_api_key?: boolean;
  has_binance_api_secret?: boolean;
}

export function normalizeBrokerApiPath(path: string): string {
  const p = path.trim();
  if (p.startsWith("/api/")) return p.slice(4);
  return p.startsWith("/") ? p : `/${p}`;
}

export function resolveBrokerEditFormPath(
  cap: BrokerCapabilities | null | undefined
): string | null {
  const ep = cap?.auth?.edit_form_endpoint;
  if (typeof ep === "string" && ep.trim()) {
    return normalizeBrokerApiPath(ep);
  }
  if (cap?.broker_name && cap.broker_name.toLowerCase() === "binance") {
    return "/broker/credentials/form";
  }
  return null;
}

export const fetchBrokerCredentialsForm = async (
  brokerName: string,
  formPath?: string
): Promise<Record<string, unknown>> => {
  const basePath = normalizeBrokerApiPath(formPath || "/broker/credentials/form");
  const sep = basePath.includes("?") ? "&" : "?";
  const response = await authenticatedFetch(
    `${basePath}${sep}broker_name=${encodeURIComponent(brokerName)}`
  );
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to load credential form: ${response.status}`);
  }
  return response.json();
};

/**
 * GET /broker/credentials — saved credentials for a broker (non-secrets).
 * Returns null when nothing is saved yet (typical before first connect/import).
 * Never throws for “not connected”; callers should treat null as the empty connect form.
 */
export const getBrokerCredentials = async (
  brokerName: string
): Promise<BrokerCredentialRecord | null> => {
  try {
    const response = await authenticatedFetch(
      `/broker/credentials?broker_name=${encodeURIComponent(brokerName)}`
    );
    if (!response.ok) {
      // 404 and other non-OK responses mean no usable credential record yet.
      return null;
    }
    return response.json();
  } catch {
    // Probe failures (network/CORS) must not toast during connect — user may simply
    // have never imported; show the empty setup form instead.
    return null;
  }
};

export const saveBrokerCredentials = async (
  brokerName: string,
  values: Record<string, unknown>
): Promise<void> => {
  const response = await authenticatedFetch("/broker/credentials", {
    method: "POST",
    body: JSON.stringify({
      ...values,
      broker_name: brokerName,
    }),
  });
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to save credentials: ${response.status}`);
  }
};

export interface DeleteBrokerCredentialsResponse {
  deleted_import_templates?: number;
}

export const deleteBrokerCredentials = async (
  brokerName: string
): Promise<DeleteBrokerCredentialsResponse> => {
  const response = await authenticatedFetch(
    `/broker/credentials?broker_name=${encodeURIComponent(brokerName)}`,
    { method: "DELETE" }
  );
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to delete credentials: ${response.status}`);
  }
  return response.json().catch(() => ({}));
};

export type BrokerTestConnectionResult = {
  success: boolean;
  message: string;
  action?: string;
  error?: string;
};

function parseBrokerTestConnectionBody(
  data: Record<string, unknown>,
  fallbackMessage: string
): Pick<BrokerTestConnectionResult, "message" | "action" | "error"> {
  const parsed = parseApiErrorBody(data, fallbackMessage);
  return {
    message: parsed.message?.trim() || fallbackMessage,
    action: parsed.action,
    error: parsed.slug,
  };
}

/**
 * POST /broker/test-connection with a JSON body.
 * `broker_name` must match the broker under test (e.g. oanda, ibkr, binance, liquidbrokers);
 * it is sent last so it cannot be overridden by fields in `values` (routing uses credentials.broker_name).
 */
export const testBrokerConnection = async (
  brokerName: string,
  values: Record<string, unknown>
): Promise<BrokerTestConnectionResult> => {
  const response = await authenticatedFetch("/broker/test-connection", {
    method: "POST",
    skipSessionExpiredOn401: true,
    body: JSON.stringify({
      ...values,
      broker_name: brokerName,
    }),
  });

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  const invalidCredsFallback =
    "Credentials could not be validated. Check your API keys and try again.";
  const httpFallback =
    response.status === 401 || response.status === 403
      ? invalidCredsFallback
      : `Failed to test connection: ${response.status}`;

  if (!response.ok) {
    const parsed = parseBrokerTestConnectionBody(data, httpFallback);
    return { success: false, ...parsed };
  }

  const success = Boolean(data.success);
  const parsed = parseBrokerTestConnectionBody(
    data,
    success ? "Connection successful" : invalidCredsFallback
  );
  return {
    success,
    message:
      parsed.message ||
      (typeof data.message === "string" ? data.message : "") ||
      (success ? "Connection successful" : invalidCredsFallback),
    action: parsed.action,
    error: parsed.error,
  };
};

export const fetchBrokerAccounts = async (brokerName: string): Promise<BrokerAccount[]> => {
  const slug = brokerName.trim().toLowerCase();
  if (slug === "custom_csv") {
    return [];
  }
  const response = await authenticatedFetch(
    `/broker/accounts?broker_name=${encodeURIComponent(brokerName)}`
  );
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to load accounts: ${response.status}`);
  }
  const data = await response.json();
  const list = Array.isArray(data) ? data : data?.accounts || [];
  return list.map((account: any) => ({
    accountCode: account.accountCode || account.account_code,
    accountStatus: account.accountStatus || account.account_status,
    baseCurrency: account.baseCurrency || account.base_currency,
    balance: account.balance ?? account.Balance ?? null,
    isDemo: account.isDemo ?? account.is_demo,
    customName: account.customName ?? account.custom_name ?? null,
  }));
};

/**
 * PUT /broker/accounts/{account_code}/custom-name?broker_name=…
 * Display name for any broker account row (OANDA, Liquid, etc.).
 */
export const updateBrokerAccountCustomName = async (
  brokerName: string,
  accountCode: string,
  customName: string | null
): Promise<void> => {
  const encoded = encodeURIComponent(accountCode);
  const response = await authenticatedFetch(
    `/broker/accounts/${encoded}/custom-name?broker_name=${encodeURIComponent(
      brokerName.toLowerCase()
    )}`,
    {
      method: "PUT",
      body: JSON.stringify({ customName, custom_name: customName }),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throwBrokerIntegrationFromBody(
      response.status,
      err,
      `Failed to update custom name: ${response.status}`
    );
  }
};

export const setActiveBrokerAccount = async (
  brokerName: string,
  accountCode: string
): Promise<void> => {
  const response = await authenticatedFetch(
    `/broker/credentials/account?broker_name=${encodeURIComponent(
      brokerName
    )}&account_code=${encodeURIComponent(accountCode)}`,
    { method: "PUT" }
  );
  if (!response.ok) {
    await throwIfNotOk(response, `Failed to update account: ${response.status}`);
  }
};

export interface CtraderOAuthAccount {
  /** ctid — sync / active account / trades filter */
  accountCode: string;
  ctidTraderAccountId?: string;
  /** Platform login (user-facing account number) */
  traderLogin?: number;
  login?: number;
  executingBrokerName?: string;
  brokerName?: string;
  brokerTitle?: string;
  depositAssetName?: string;
  isLive?: boolean;
  environment?: "demo" | "live";
  accountStatus?: string;
  baseCurrency?: string;
}

export interface CtraderOAuthAuthorizeResponse {
  authorize_url: string;
  scope?: string;
  redirect_uri?: string;
  environment?: "demo" | "live";
}

export interface CtraderOAuthCallbackResponse {
  success: boolean;
  broker_name: string;
  /** ctid */
  account_code: string;
  trader_login?: number | null;
  executing_broker_name?: string | null;
  accounts: CtraderOAuthAccount[];
  credentials_id?: string;
  expires_at?: string;
}

function mapCtraderOAuthAccount(raw: Record<string, unknown>): CtraderOAuthAccount {
  const environmentRaw = String(raw.environment ?? "").trim().toLowerCase();
  const ctid = String(
    raw.accountCode ??
      raw.account_code ??
      raw.ctidTraderAccountId ??
      raw.ctid_trader_account_id ??
      ""
  ).trim();
  const traderLoginRaw = raw.trader_login ?? raw.traderLogin ?? raw.login;
  const traderLogin =
    traderLoginRaw != null && traderLoginRaw !== "" ? Number(traderLoginRaw) : undefined;
  const executingBrokerName =
    raw.executing_broker_name != null
      ? String(raw.executing_broker_name)
      : raw.executingBrokerName != null
        ? String(raw.executingBrokerName)
        : raw.brokerName != null
          ? String(raw.brokerName)
          : raw.broker_name != null
            ? String(raw.broker_name)
            : raw.brokerTitle != null
              ? String(raw.brokerTitle)
              : raw.broker_title != null
                ? String(raw.broker_title)
                : undefined;
  return {
    accountCode: ctid,
    ctidTraderAccountId: String(raw.ctidTraderAccountId ?? raw.ctid_trader_account_id ?? ctid).trim(),
    traderLogin: Number.isFinite(traderLogin) ? traderLogin : undefined,
    login: Number.isFinite(traderLogin) ? traderLogin : undefined,
    executingBrokerName,
    brokerName: executingBrokerName,
    brokerTitle:
      raw.brokerTitle != null
        ? String(raw.brokerTitle)
        : raw.broker_title != null
          ? String(raw.broker_title)
          : undefined,
    depositAssetName:
      raw.depositAssetName != null
        ? String(raw.depositAssetName)
        : raw.deposit_asset_name != null
          ? String(raw.deposit_asset_name)
          : undefined,
    isLive: raw.isLive ?? raw.is_live,
    environment: environmentRaw === "demo" || environmentRaw === "live" ? environmentRaw : undefined,
    accountStatus:
      raw.accountStatus != null
        ? String(raw.accountStatus)
        : raw.account_status != null
          ? String(raw.account_status)
          : undefined,
    baseCurrency:
      raw.baseCurrency != null
        ? String(raw.baseCurrency)
        : raw.base_currency != null
          ? String(raw.base_currency)
          : undefined,
  };
}

export const fetchCtraderOAuthAuthorizeUrl = async (
  authorizePath = "/broker/ctrader/oauth/authorize-url",
  environment?: "demo" | "live"
): Promise<CtraderOAuthAuthorizeResponse> => {
  const basePath = normalizeBrokerApiPath(authorizePath);
  const path = environment
    ? `${basePath}${basePath.includes("?") ? "&" : "?"}environment=${encodeURIComponent(
        environment
      )}`
    : basePath;
  const response = await authenticatedFetch(path);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwBrokerIntegrationFromBody(
      response.status,
      error,
      `Failed to start cTrader OAuth: ${response.status}`
    );
  }
  const data = await response.json();
  const authorizeUrl = data.authorize_url ?? data.authorizeUrl;
  if (!authorizeUrl || typeof authorizeUrl !== "string") {
    throwBrokerClientError("Broker did not return an OAuth authorize URL.");
  }
  return {
    authorize_url: authorizeUrl,
    scope: data.scope,
    redirect_uri: data.redirect_uri ?? data.redirectUri,
    environment:
      String(data.environment ?? "")
        .trim()
        .toLowerCase() === "live"
        ? "live"
        : String(data.environment ?? "")
              .trim()
              .toLowerCase() === "demo"
          ? "demo"
          : undefined,
  };
};

export const completeCtraderOAuthCallback = async (
  body: {
    code: string;
    traded_asset_classes: string[];
    ctid_trader_account_id?: string;
    environment?: "demo" | "live";
    md_fetch_consent?: boolean;
  },
  callbackPath = "/broker/ctrader/oauth/callback"
): Promise<CtraderOAuthCallbackResponse> => {
  const path = normalizeBrokerApiPath(callbackPath);
  const response = await authenticatedFetch(path, {
    method: "POST",
    body: JSON.stringify({
      code: body.code,
      traded_asset_classes: body.traded_asset_classes,
      ...(body.environment ? { environment: body.environment } : {}),
      ...(body.ctid_trader_account_id
        ? { ctid_trader_account_id: body.ctid_trader_account_id }
        : {}),
      ...(body.md_fetch_consent ? { md_fetch_consent: true } : {}),
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwBrokerIntegrationFromBody(
      response.status,
      error,
      `cTrader OAuth connect failed: ${response.status}`
    );
  }
  const data = await response.json();
  const accountsRaw = Array.isArray(data.accounts) ? data.accounts : [];
  const accounts = accountsRaw
    .map((item: unknown) =>
      item && typeof item === "object" ? mapCtraderOAuthAccount(item as Record<string, unknown>) : null
    )
    .filter((a: CtraderOAuthAccount | null): a is CtraderOAuthAccount => !!a?.accountCode);

  const traderLoginRaw = data.trader_login ?? data.traderLogin;
  const trader_login =
    traderLoginRaw != null && traderLoginRaw !== "" ? Number(traderLoginRaw) : null;

  return {
    success: Boolean(data.success ?? true),
    broker_name: String(data.broker_name ?? data.brokerName ?? "ctrader"),
    account_code: String(data.account_code ?? data.accountCode ?? accounts[0]?.accountCode ?? ""),
    trader_login: Number.isFinite(trader_login) ? trader_login : null,
    executing_broker_name:
      data.executing_broker_name != null
        ? String(data.executing_broker_name)
        : data.executingBrokerName != null
          ? String(data.executingBrokerName)
          : null,
    accounts,
    credentials_id: data.credentials_id ?? data.credentialsId,
    expires_at: data.expires_at ?? data.expiresAt,
  };
};

export const syncBrokerTrades = async (
  brokerName: string,
  accountCode?: string | null
): Promise<BrokerSyncWithInstrumentSetupResponse | null> => {
  const params = new URLSearchParams({
    broker_name: brokerName,
    save_to_db: "true",
  });
  const code = (accountCode || "").trim();
  if (code) {
    params.set("account_code", code);
  }
  const response = await authenticatedFetch(`/broker/trades?${params.toString()}`);
  if (!response.ok) {
    await throwBrokerResponseNotOk(response, `Failed to sync trades: ${response.status}`);
  }
  const text = await response.text().catch(() => "");
  if (!text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/** Extra multipart parts on statement import (e.g. Tradovate `cash_history_file`). */
export type BrokerImportFormFilePart = {
  fieldName: string;
  file: File;
};

export const uploadBrokerCsvImport = async (
  brokerName: string,
  file: File,
  queryParams: Record<string, string | string[]>,
  extraFormFiles?: BrokerImportFormFilePart[],
  options?: { endpoint?: string }
): Promise<any> => {
  const token = await getClerkToken();
  if (!token) {
    throw new ApiRequestError("Authentication required. Please sign in.", 401, {
      slug: "auth.required",
      message: "Authentication required. Please sign in.",
    });
  }

  const fromCapabilities = options?.endpoint?.trim();
  const endpoint = fromCapabilities
    ? normalizeBrokerApiPath(fromCapabilities)
    : BROKER_STATEMENT_IMPORT_ENDPOINTS[brokerName.toLowerCase()];
  if (!endpoint) {
    throwBrokerClientError(`Statement import is not configured for broker: ${brokerName}`);
  }

  const params = new URLSearchParams();
  const omitBrokerNameQuery =
    brokerName.toLowerCase() === "hyperliquid" || endpoint.includes("/hyperliquid/csv-import");
  if (!omitBrokerNameQuery) {
    params.set("broker_name", brokerName);
  }
  for (const [key, value] of Object.entries(queryParams)) {
    if (value == null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        const trimmed = String(item).trim();
        if (trimmed) params.append(key, trimmed);
      }
    } else {
      const trimmed = String(value).trim();
      if (trimmed) params.set(key, trimmed);
    }
  }

  const formData = new FormData();
  formData.append("file", file);
  for (const part of extraFormFiles ?? []) {
    if (part?.file && part.fieldName) {
      formData.append(part.fieldName, part.file);
    }
  }

  /** Same base as `authenticatedFetch` — do not hardcode `/api` or CSV POSTs miss `REACT_APP_BACKEND_API_URL`. */
  const url = `${BACKEND_API_URL}${endpoint}?${params.toString()}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    await throwBrokerResponseNotOk(
      response,
      `Import failed (${response.status}). If your backend URL is set via REACT_APP_BACKEND_API_URL, this request uses that base (same as other API calls).`
    );
  }

  return response.json();
};

/** GET /api/broker/sync-status — poll after resync until analytics are safe to load. */
export type BrokerSyncStatusResponse = {
  broker_name: string;
  account_code: string;
  phase?: "syncing" | "needs_instrument_setup" | "ready" | string;
  inflight: boolean;
  inflight_ttl_seconds: number | null;
  instrument_setup_pending?: boolean | null;
  requires_instrument_confirmation?: boolean | null;
  instrument_setup_url?: string | null;
  ready_for_analytics: boolean;
  last_broker_sync_at: string | null;
  closed_trades: number;
  open_trades: number;
};

export const fetchBrokerSyncStatus = async (
  accountCode: string,
  brokerName: string
): Promise<BrokerSyncStatusResponse> => {
  const code = accountCode.trim();
  const broker = brokerName.trim().toLowerCase();
  if (!code || !broker) {
    throwBrokerClientError("account_code and broker_name are required for sync status.");
  }

  const params = new URLSearchParams({
    account_code: code,
    broker_name: broker,
  });
  const response = await authenticatedFetch(`/broker/sync-status?${params.toString()}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiRequestError(
        "Trade account not found or not allowed for this user.",
        404,
        {
          slug: "broker.account_not_found",
          message: "Trade account not found or not allowed for this user.",
        }
      );
    }
    await throwBrokerResponseNotOk(response, `Failed to fetch sync status: ${response.status}`);
  }

  return (await response.json()) as BrokerSyncStatusResponse;
};
